import { useEffect, useRef, useState } from 'react';
import { getHome } from '../../api/home';
import { uploadMemoryAudio } from '../../api/memories';
import type { HomeResponse, MemoryRead } from '../../api/types';
import { useAnonymousUser } from '../../hooks/useAnonymousUser';
import WelcomeScreen from './components/WelcomeScreen';
import RecordingScreen from './components/RecordingScreen';
import AnalysisScreen from './components/AnalysisScreen';
import IntroBiographyScreen from './components/IntroBiographyScreen';
import IntroEmotionsScreen from './components/IntroEmotionsScreen';
import IntroTimelineScreen from './components/IntroTimelineScreen';
import IntroPhotosScreen from './components/IntroPhotosScreen';
import FirstMemoryScreen from './components/FirstMemoryScreen';
import AppShell from './components/AppShell';

type Screen =
  | 'boot'
  | 'welcome'
  | 'recording'
  | 'analysis'
  | 'intro-bio'
  | 'intro-emotions'
  | 'intro-timeline'
  | 'intro-photos'
  | 'first-memory'
  | 'app';

const onboardingCompletedKey = (userId: string) => `omut:onboarding-completed:${userId}`;

function getOnboardingCompleted(userId: string): boolean {
  try {
    return localStorage.getItem(onboardingCompletedKey(userId)) === 'true';
  } catch {
    return false;
  }
}

function setOnboardingCompleted(userId: string): void {
  try {
    localStorage.setItem(onboardingCompletedKey(userId), 'true');
  } catch {
    // Blocked WebView storage should not prevent the app from opening.
  }
}

function clearOnboardingCompleted(userId: string): void {
  try {
    localStorage.removeItem(onboardingCompletedKey(userId));
  } catch {
    // Ignore blocked WebView storage.
  }
}

export default function Home() {
  const [screen, setScreen] = useState<Screen>('boot');
  const { userId, loading: userLoading, error: userError } = useAnonymousUser();
  const [firstAudio, setFirstAudio] = useState<Blob | null>(null);
  const [firstAudioDuration, setFirstAudioDuration] = useState(0);
  const [firstMemory, setFirstMemory] = useState<MemoryRead | null>(null);
  const [initialHomeData, setInitialHomeData] = useState<HomeResponse | null>(null);
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'uploading' | 'completed' | 'failed'>('idle');
  const [processingError, setProcessingError] = useState<string | null>(null);
  const processingStartedRef = useRef(false);

  const next = (to: Screen) => () => setScreen(to);

  const handleRecordingComplete = (audioBlob: Blob, durationSeconds: number) => {
    setFirstAudio(audioBlob);
    setFirstAudioDuration(durationSeconds);
    setFirstMemory(null);
    setProcessingStatus('idle');
    setProcessingError(null);
    processingStartedRef.current = false;
    setScreen('analysis');
  };

  const handleWelcomeLogin = async (loggedUserId: string) => {
    setProcessingError(null);
    try {
      const home = await getHome(loggedUserId);
      setInitialHomeData(home);
    } catch {
      setInitialHomeData(null);
    }
    setOnboardingCompleted(loggedUserId);
    setScreen('app');
  };

  useEffect(() => {
    if (!userId || screen !== 'boot') return;

    let cancelled = false;
    const hasLocalCompletion = getOnboardingCompleted(userId);

    getHome(userId)
      .then((home) => {
        if (cancelled) return;
        setInitialHomeData(home);
        if (home.has_memories) {
          setOnboardingCompleted(userId);
          setScreen('app');
        } else {
          clearOnboardingCompleted(userId);
          setScreen('welcome');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setScreen(hasLocalCompletion ? 'app' : 'welcome');
      });

    return () => {
      cancelled = true;
    };
  }, [screen, userId]);

  useEffect(() => {
    if (screen === 'boot' && !userLoading && !userId) {
      setScreen('welcome');
    }
  }, [screen, userId, userLoading]);

  useEffect(() => {
    if (screen !== 'analysis' || !userId || !firstAudio || processingStartedRef.current) return;

    processingStartedRef.current = true;
    setProcessingStatus('uploading');
    setProcessingError(null);

    uploadMemoryAudio({
      userId,
      audioBlob: firstAudio,
      durationSeconds: firstAudioDuration,
      processSync: true,
    })
      .then((result) => {
        setFirstMemory(result.memory);
        if (result.memory.status === 'completed') {
          setOnboardingCompleted(userId);
          setProcessingStatus('completed');
        } else {
          setProcessingStatus('failed');
          setProcessingError(result.memory.status === 'failed'
            ? 'Омут Памяти не смог обработать запись. Попробуйте записать воспоминание ещё раз.'
            : 'Воспоминание сохранено, но анализ пока не завершён.');
        }
      })
      .catch((error) => {
        setProcessingStatus('failed');
        setProcessingError(error instanceof Error ? error.message : 'Не удалось обработать воспоминание');
      });
  }, [screen, userId, firstAudio, firstAudioDuration]);

  return (
    <div
      style={{
        background: '#0F1020',
        width: '100%',
        maxWidth: '100vw',
        minHeight: 'var(--omut-app-height, 100dvh)',
        height: 'var(--omut-app-height, 100dvh)',
        overflow: 'hidden',
      }}
    >
      <div
        className="relative overflow-hidden"
        style={{ width: '100%', height: '100%', maxWidth: '100vw', flexShrink: 0 }}
      >
        {screen === 'boot' && (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(170deg, #1A1B2E 0%, #2D1B3D 60%, #1F1A35 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7A94',
              fontSize: '14px',
            }}
          >
            Поднимаем Омут Памяти...
          </div>
        )}
        {screen === 'welcome' && (
          <WelcomeScreen
            onNext={next('recording')}
            onLoginSuccess={(user) => {
              void handleWelcomeLogin(user.id);
            }}
          />
        )}
        {screen === 'recording' && (
          <RecordingScreen
            onComplete={handleRecordingComplete}
            disabled={userLoading || !userId}
            disabledMessage={userError || (userLoading ? 'Готовим безопасное пространство для первой записи...' : null)}
          />
        )}
        {screen === 'analysis' && (
          <AnalysisScreen
            status={processingStatus}
            error={processingError}
            onNext={next('intro-bio')}
          />
        )}
        {screen === 'intro-bio' && <IntroBiographyScreen onNext={next('intro-emotions')} />}
        {screen === 'intro-emotions' && <IntroEmotionsScreen onNext={next('intro-timeline')} />}
        {screen === 'intro-timeline' && <IntroTimelineScreen onNext={next('intro-photos')} />}
        {screen === 'intro-photos' && <IntroPhotosScreen onNext={next('first-memory')} />}
        {screen === 'first-memory' && (
          <FirstMemoryScreen
            memory={firstMemory}
            status={processingStatus}
            error={processingError}
            durationSeconds={firstAudioDuration}
            onRetry={() => {
              setFirstMemory(null);
              setProcessingStatus('idle');
              setProcessingError(null);
              processingStartedRef.current = false;
              setScreen('recording');
            }}
            onNext={next('app')}
          />
        )}
        {screen === 'app' && <AppShell initialHomeData={initialHomeData} />}
      </div>
    </div>
  );
}
